import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import api, { getApiBase } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Crown, Check, Zap, Users, Infinity, Gift, Package, Sparkles, XCircle, RotateCcw } from "lucide-react";
import { toast } from "sonner";

function subscriptionFromUser(user) {
  if (!user || user.role !== "coach") return null;
  return {
    subscription: user.subscription ?? null,
    is_active: !!user.has_active_subscription,
    trial_available: user.trial_available ?? true,
  };
}

export default function CoachSubscription() {
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [currentSub, setCurrentSub] = useState(() => subscriptionFromUser(user));
  const [billing, setBilling] = useState("monthly");
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!currentSub && user?.role === "coach") {
      setCurrentSub(subscriptionFromUser(user));
    }
  }, [user, currentSub]);

  const loadData = async () => {
    setLoading(true);

    try {
      // Public endpoint — no auth cookies (stale session_token caused 401 before backend fix)
      const { data } = await axios.get(`${getApiBase()}/api/subscriptions/plans`, {
        withCredentials: false,
        timeout: 20000,
      });
      setPlans(data);
    } catch (err) {
      console.error(err);
      toast.error("Impossible de charger les formules d'abonnement");
    }

    try {
      const { data } = await api.get("/subscriptions/current");
      setCurrentSub(data);
    } catch (err) {
      console.error(err);
      const fallback = subscriptionFromUser(user);
      if (fallback) setCurrentSub(fallback);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setCancelLoading(true);
    try {
      const { data } = await api.post("/subscriptions/cancel");
      setCurrentSub((prev) => ({
        ...prev,
        subscription: data.subscription,
        is_active: data.is_active,
      }));
      toast.success(data.message || "Abonnement programmé pour annulation");
    } catch (err) {
      toast.error(err.response?.data?.error || "Impossible d'annuler l'abonnement");
    } finally {
      setCancelLoading(false);
    }
  };

  const handleReactivate = async () => {
    setReactivateLoading(true);
    try {
      const { data } = await api.post("/subscriptions/reactivate");
      setCurrentSub((prev) => ({
        ...prev,
        subscription: data.subscription,
        is_active: data.is_active,
      }));
      toast.success(data.message || "Abonnement réactivé");
    } catch (err) {
      toast.error(err.response?.data?.error || "Impossible de réactiver l'abonnement");
    } finally {
      setReactivateLoading(false);
    }
  };

  const handleCheckout = async (planType) => {
    setCheckoutLoading(planType);
    try {
      const { data } = await api.post("/subscriptions/checkout", {
        plan_type: planType,
        billing_cycle: billing,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error("Erreur lors de la création du paiement");
    } finally {
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  const isActive = currentSub?.is_active;
  const activePlan = currentSub?.subscription?.plan_type;
  const isTrialing = currentSub?.subscription?.is_trialing;
  const isPendingCancellation = currentSub?.subscription?.is_pending_cancellation;
  const periodEndDate = currentSub?.subscription?.current_period_end
    ? new Date(currentSub.subscription.current_period_end)
    : null;
  const trialAvailable = currentSub?.trial_available;

  const getButtonLabel = (planType) => {
    if (checkoutLoading === planType) return "Chargement...";
    if (activePlan === planType) return "Plan actuel";
    if (trialAvailable) return "Essayer 14 jours gratuitement";
    return `Choisir ${planType === "pro" ? "Pro" : "Basic"}`;
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="text-center">
        <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">
          Abonnement Coach
        </h1>
        <p className="mt-2 text-slate-500">
          Choisissez votre formule pour commencer à accompagner vos élèves
        </p>
      </div>

      {/* Statut actuel */}
      {isActive && (() => {
        const planLabel = currentSub.subscription.plan_type === "pro" ? "Pro" : "Basic";
        const trialEnd = currentSub.subscription.trial_ends_at ? new Date(currentSub.subscription.trial_ends_at) : null;
        const daysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - new Date()) / (1000 * 60 * 60 * 24))) : 0;

        return isTrialing ? (
          <Card className={`${isPendingCancellation ? "border-amber-200 bg-amber-50/40" : "border-purple-200 bg-gradient-to-r from-purple-50 to-purple-50/30"}`}>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div className={`h-12 w-12 rounded-full flex items-center justify-center ${isPendingCancellation ? "bg-amber-100" : "bg-purple-100"}`}>
                  <Gift className={`w-6 h-6 ${isPendingCancellation ? "text-amber-600" : "text-purple-600"}`} />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-slate-900">Essai gratuit — {planLabel}</p>
                    <Badge variant="outline" className={isPendingCancellation ? "border-amber-300 text-amber-800 bg-amber-50" : "border-purple-300 text-purple-700 bg-purple-50"}>
                      {isPendingCancellation ? "Fin programmée" : "Essai"}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {isPendingCancellation ? (
                      <>Accès jusqu&apos;au <strong className="text-amber-700">{periodEndDate?.toLocaleDateString("fr-FR")}</strong> — aucune facturation</>
                    ) : daysLeft > 0 ? (
                      <><strong className="text-purple-600">{daysLeft} jour{daysLeft > 1 ? "s" : ""}</strong> restant{daysLeft > 1 ? "s" : ""} — fin le {trialEnd.toLocaleDateString("fr-FR")}</>
                    ) : (
                      <>Essai termin&eacute; — la facturation d&eacute;marre</>
                    )}
                  </p>
                </div>
              </div>
              {!isPendingCancellation && (
                <div>
                  <div className="flex justify-between text-xs text-slate-400 mb-1">
                    <span>D&eacute;but de l&apos;essai</span>
                    <span>Facturation automatique</span>
                  </div>
                  <div className="h-2 bg-purple-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-purple-500 rounded-full transition-all"
                      style={{ width: `${Math.max(0, Math.min(100, ((14 - daysLeft) / 14) * 100))}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1.5">
                    Puis {currentSub.subscription.amount}&euro;/{currentSub.subscription.billing_cycle === "annual" ? "an" : "mois"} automatiquement
                  </p>
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-1 border-t border-slate-200/80">
                {isPendingCancellation ? (
                  <Button variant="outline" size="sm" className="rounded-full" onClick={handleReactivate} disabled={reactivateLoading}>
                    <RotateCcw className={`w-4 h-4 mr-1.5 ${reactivateLoading ? "animate-spin" : ""}`} />
                    {reactivateLoading ? "Réactivation..." : "Réactiver l'essai / abonnement"}
                  </Button>
                ) : (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
                        <XCircle className="w-4 h-4 mr-1.5" />
                        Annuler avant facturation
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Arrêter l&apos;essai gratuit ?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Votre accès reste actif jusqu&apos;au {trialEnd?.toLocaleDateString("fr-FR")} sans aucun prélèvement.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Continuer l&apos;essai</AlertDialogCancel>
                        <AlertDialogAction onClick={handleCancel} className="bg-red-600 hover:bg-red-700">
                          {cancelLoading ? "Annulation..." : "Confirmer"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className={`${isPendingCancellation ? "border-amber-200 bg-amber-50/40" : "border-blue-200 bg-blue-50/50"}`}>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div className={`h-12 w-12 rounded-full flex items-center justify-center ${isPendingCancellation ? "bg-amber-100" : "bg-blue-100"}`}>
                  <Crown className={`w-6 h-6 ${isPendingCancellation ? "text-amber-600" : "text-blue-600"}`} />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <p className="font-semibold text-slate-900">Abonnement {planLabel} actif</p>
                  <p className="text-sm text-slate-500">
                    {isPendingCancellation ? (
                      <>
                        Se termine le{" "}
                        <strong className="text-amber-700">
                          {periodEndDate?.toLocaleDateString("fr-FR")}
                        </strong>
                        {" — "}aucun renouvellement
                      </>
                    ) : (
                      <>
                        Prochain renouvellement le{" "}
                        {periodEndDate?.toLocaleDateString("fr-FR")}
                        {" — "}
                        {currentSub.subscription.billing_cycle === "annual" ? "Annuel" : "Mensuel"}
                      </>
                    )}
                  </p>
                </div>
                <Badge
                  variant="outline"
                  className={
                    isPendingCancellation
                      ? "border-amber-300 text-amber-800 bg-amber-50"
                      : "border-green-300 text-green-700 bg-green-50"
                  }
                >
                  {isPendingCancellation ? "Fin programmée" : "Actif"}
                </Badge>
              </div>

              <div className="flex flex-wrap gap-2 pt-1 border-t border-slate-200/80">
                {isPendingCancellation ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    onClick={handleReactivate}
                    disabled={reactivateLoading}
                    data-testid="subscription-reactivate-btn"
                  >
                    <RotateCcw className={`w-4 h-4 mr-1.5 ${reactivateLoading ? "animate-spin" : ""}`} />
                    {reactivateLoading ? "Réactivation..." : "Réactiver mon abonnement"}
                  </Button>
                ) : (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                        data-testid="subscription-cancel-btn"
                      >
                        <XCircle className="w-4 h-4 mr-1.5" />
                        Annuler l&apos;abonnement
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Annuler votre abonnement ?</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                          <div className="space-y-2 text-sm text-slate-500">
                            <p>
                              Votre accès reste actif jusqu&apos;au{" "}
                              <strong className="text-slate-800">
                                {periodEndDate?.toLocaleDateString("fr-FR")}
                              </strong>
                              . Aucun nouveau prélèvement ne sera effectué après cette date.
                            </p>
                            <p>Vous pourrez réactiver votre abonnement à tout moment avant cette échéance.</p>
                          </div>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Conserver l&apos;abonnement</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleCancel}
                          disabled={cancelLoading}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          {cancelLoading ? "Annulation..." : "Confirmer l'annulation"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Toggle mensuel/annuel */}
      <div className="flex items-center justify-center gap-3">
        <button
          onClick={() => setBilling("monthly")}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            billing === "monthly"
              ? "bg-blue-600 text-white"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
        >
          Mensuel
        </button>
        <button
          onClick={() => setBilling("annual")}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            billing === "annual"
              ? "bg-blue-600 text-white"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
        >
          Annuel
          <span className="ml-1.5 text-xs font-bold text-orange-500">-2 mois</span>
        </button>
      </div>

      {/* Plans */}
      {!plans && !loading && (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center space-y-4">
            <p className="text-slate-500">Les formules n'ont pas pu être chargées.</p>
            <Button onClick={loadData} variant="outline" className="rounded-full">
              Réessayer
            </Button>
          </CardContent>
        </Card>
      )}

      {plans && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* BASIC */}
          <Card
            className={`relative overflow-hidden transition-shadow hover:shadow-lg ${
              activePlan === "basic" ? "ring-2 ring-blue-500" : ""
            }`}
          >
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2">
                <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Basic</CardTitle>
                  <CardDescription>{plans.basic.description}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <span className="text-4xl font-bold text-slate-900">
                  {billing === "annual"
                    ? plans.basic.prices.annual_monthly.toFixed(2)
                    : plans.basic.prices.monthly.toFixed(2)}
                  &euro;
                </span>
                <span className="text-slate-500">/mois</span>
                {billing === "annual" && (
                  <p className="text-sm text-slate-400 mt-1">
                    Factur&eacute; {plans.basic.prices.annual.toFixed(2)}&euro;/an
                  </p>
                )}
              </div>

              {/* Badge essai 14 jours */}
              {trialAvailable && !activePlan && (
                <div className="inline-flex items-center gap-1.5 text-sm font-medium text-purple-600 bg-purple-50 px-3 py-1.5 rounded-full">
                  <Gift className="w-3.5 h-3.5" /> 14 jours d'essai gratuit
                </div>
              )}

              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-sm">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>
                    <Users className="w-4 h-4 inline mr-1" />
                    Jusqu'à <strong>5 élèves</strong>
                  </span>
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>
                    <Package className="w-4 h-4 inline mr-1" />
                    Jusqu'à <strong>5 forfaits</strong> personnalisés
                  </span>
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Plans diététiques & entraînement
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Messagerie
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Calendrier des séances
                </li>
              </ul>

              <Button
                onClick={() => handleCheckout("basic")}
                disabled={activePlan === "basic" || checkoutLoading === "basic"}
                className={`w-full rounded-full h-11 ${trialAvailable && !activePlan ? "bg-purple-600 hover:bg-purple-700" : ""}`}
                variant={activePlan === "basic" ? "outline" : "default"}
              >
                {getButtonLabel("basic")}
              </Button>
              {trialAvailable && !activePlan && (
                <p className="text-xs text-slate-400 text-center -mt-3">
                  0&euro; pendant 14 jours, puis facturation automatique
                </p>
              )}
            </CardContent>
          </Card>

          {/* PRO */}
          <Card
            className={`relative overflow-hidden transition-shadow hover:shadow-lg border-orange-200 ${
              activePlan === "pro" ? "ring-2 ring-orange-500" : ""
            }`}
          >
            <div className="absolute top-0 right-0 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-bl-xl">
              POPULAIRE
            </div>
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2">
                <div className="h-10 w-10 rounded-xl bg-orange-100 flex items-center justify-center">
                  <Crown className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Pro</CardTitle>
                  <CardDescription>{plans.pro.description}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <span className="text-4xl font-bold text-slate-900">
                  {billing === "annual"
                    ? plans.pro.prices.annual_monthly.toFixed(2)
                    : plans.pro.prices.monthly.toFixed(2)}
                  &euro;
                </span>
                <span className="text-slate-500">/mois</span>
                {billing === "annual" && (
                  <p className="text-sm text-slate-400 mt-1">
                    Factur&eacute; {plans.pro.prices.annual.toFixed(2)}&euro;/an
                  </p>
                )}
              </div>

              {/* Badge essai 14 jours */}
              {trialAvailable && !activePlan && (
                <div className="inline-flex items-center gap-1.5 text-sm font-medium text-purple-600 bg-purple-50 px-3 py-1.5 rounded-full">
                  <Gift className="w-3.5 h-3.5" /> 14 jours d'essai gratuit
                </div>
              )}

              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-sm">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>
                    <Infinity className="w-4 h-4 inline mr-1" />
                    Élèves <strong>illimités</strong>
                  </span>
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>
                    <Package className="w-4 h-4 inline mr-1" />
                    Forfaits personnalisés <strong>illimités</strong>
                  </span>
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Plans diététiques & entraînement
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Messagerie
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Calendrier des séances
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Paiements élèves via Stripe
                </li>
                <li className="flex items-center gap-3 text-sm font-medium">
                  <Sparkles className="w-4 h-4 text-purple-500 flex-shrink-0" />
                  <span className="text-purple-700">Assistant IA — plans, programmes & tarifs</span>
                </li>
              </ul>

              <Button
                onClick={() => handleCheckout("pro")}
                disabled={activePlan === "pro" || checkoutLoading === "pro"}
                className={`w-full rounded-full h-11 ${trialAvailable && !activePlan ? "bg-purple-600 hover:bg-purple-700" : "bg-orange-500 hover:bg-orange-600"}`}
                variant={activePlan === "pro" ? "outline" : "default"}
              >
                {getButtonLabel("pro")}
              </Button>
              {trialAvailable && !activePlan && (
                <p className="text-xs text-slate-400 text-center -mt-3">
                  0&euro; pendant 14 jours, puis facturation automatique
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
